import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Notify, toast } from "@/components/ui/notify";
import { Card, CardContent } from "@/components/ui/card";
import { ProductionLineCanvas } from "@/features/lines/canvas/ProductionLineCanvas";
import { MachineSidebar } from "@/features/machine-sidebar/MachineSidebar";
import { useCreateMachine, useLineDetail, useUpdateLine } from "@/features/line-editor/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import {
  LineHeaderForm,
  type LineHeaderValues,
} from "@/features/lines/editor/components/LineHeaderForm";
import { LineCalendarEditor } from "@/features/lines/calendar";
import { LineChainEditor } from "@/features/lines/chain";
import type { LineCanvas } from "@/types/canvas.types";
import { useEditorAggregate } from "./helpers";

export interface EditModeViewProps {
  /** Repassa o canvas em edição para o painel de pendências, que vive acima na árvore. */
  onEditCanvasChange?: (canvas: LineCanvas) => void;
}

export function EditModeView({ onEditCanvasChange }: EditModeViewProps) {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  void navigate;
  const lineQuery = useLineDetail(id);
  const { data: line, isLoading, isError, error, refetch } = lineQuery;
  const updateLine = useUpdateLine(id);

  // Checagem `isError` explícita (não mais só `!data`) para que a mensagem venha do
  // backend via `getFriendlyErrorMessage` em vez do texto fixo genérico.
  return line ? (
    <EditModeInner
      key={line.id + ":" + line.updatedAt}
      lineId={id}
      initial={{
        name: line.name,
        responsible: line.responsible ?? "",
        description: line.description ?? "",
        productionUnit: line.productionUnit ?? "",
      }}
      isRunning={line.status === "RUNNING"}
      updateLine={updateLine}
      onEditCanvasChange={onEditCanvasChange}
    />
  ) : isLoading ? (
    <p className="text-sm text-text-secondary">Carregando linha…</p>
  ) : isError ? (
    <Notify.QueryError
      error={error}
      onRetry={() => void refetch()}
      title="Não foi possível carregar esta linha"
    />
  ) : (
    <Card>
      <CardContent className="pt-6 text-sm text-text-secondary">
        Não foi possível carregar esta linha.
      </CardContent>
    </Card>
  );
}

interface EditInnerProps {
  lineId: string;
  initial: LineHeaderValues;
  isRunning: boolean;
  updateLine: ReturnType<typeof useUpdateLine>;
  onEditCanvasChange?: (canvas: LineCanvas) => void;
}

function EditModeInner({
  lineId: id,
  initial,
  isRunning,
  updateLine,
  onEditCanvasChange,
}: EditInnerProps) {
  const [headerValues, setHeaderValues] = useState<LineHeaderValues>(initial);
  const [headerDirty, setHeaderDirty] = useState(false);
  const aggregate = useEditorAggregate();
  const createMachine = useCreateMachine(id);
  const [pendingMachineAdd, setPendingMachineAdd] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const [sidebarState, setSidebarState] = useState<{
    nodeId: string;
    machineId: string | null;
    canvasAdd: boolean;
  } | null>(null);
  const [pendingAssignment, setPendingAssignment] = useState<{
    nodeId: string;
    machineId: string;
    name: string;
  } | null>(null);

  const onSaveOverride = useCallback(async () => {
    if (headerDirty) {
      try {
        await updateLine.mutateAsync({
          name: headerValues.name,
          description: headerValues.description,
          responsible: headerValues.responsible,
          productionUnit: headerValues.productionUnit,
          confirmRealLine: isRunning ? true : undefined,
        });
        setHeaderDirty(false);
      } catch (err) {
        const msg = getFriendlyErrorMessage(err);
        toast.error("Falha ao salvar cabeçalho.", { description: msg });
        throw err;
      }
    }
  }, [headerDirty, headerValues, updateLine, isRunning]);

  return (
    <section className="flex h-full flex-col gap-3">
      <LineHeaderForm
        mode="edit"
        values={headerValues}
        onChange={(patch) => {
          setHeaderValues((prev) => ({ ...prev, ...patch }));
          setHeaderDirty(true);
        }}
        isRunning={isRunning}
      />
      <div className="flex items-center gap-2">
        <LineCalendarEditor lineId={id} />
        <LineChainEditor lineId={id} />
      </div>
      <div className="min-h-[520px] flex-1">
        <ProductionLineCanvas
          lineId={id}
          mode="edit"
          height={520}
          isRunning={isRunning}
          saveLabel="Salvar"
          // ⚠️ SEM `externalCanSave` AQUI, e é uma decisão, não um esquecimento.
          //
          // O agregado de pendências do modo EDIÇÃO é derivado do canvas DO SERVIDOR
          // (`useLineCanvas`): ele descreve o que já está salvo, não o que está sendo editado.
          // Usá-lo para desabilitar o Salvar cria um impasse fechado — a pendência "máquina sem
          // representação no canvas" bloqueia exatamente a ação que a resolveria, e arrastar a
          // máquina não ajuda porque isso só muda o estado local. O usuário fica preso sem
          // nenhum erro no console.
          //
          // Sem esta prop, `CanvasInner` cai em `draft.validation.canSave`, que valida o canvas
          // EM EDIÇÃO: nó órfão é warning, e só erro estrutural real (máquina sem `machineId`,
          // máquina duplicada, aresta pendurada) impede o save.
          //
          // Em `CreationModeView` o gate PERMANECE, e continua certo: lá o agregado valida
          // `computeCreationIssues(draft)` — o próprio rascunho local, que é o que será salvo.
          onSaved={() => aggregate?.reset()}
          onBeforeSave={headerDirty ? onSaveOverride : undefined}
          onCreateNewMachine={(basic) => {
            void (async () => {
              try {
                const created = await createMachine.mutateAsync({
                  name: basic.name,
                  code: basic.code,
                  type: basic.type,
                  requiresOperator: false,
                });
                setPendingMachineAdd(created);
                toast.success(`Máquina "${created.name}" cadastrada.`);
              } catch (err) {
                const msg = getFriendlyErrorMessage(err);
                toast.error("Falha ao cadastrar máquina.", { description: msg });
              }
            })();
          }}
          pendingMachineAdd={pendingMachineAdd}
          onMachineAddApplied={() => setPendingMachineAdd(null)}
          onEditCanvasChange={onEditCanvasChange}
          onOpenSidebar={(node, options) => {
            setSidebarState({
              nodeId: node.id,
              machineId: (node.data?.machineId as string | undefined) ?? null,
              canvasAdd: options?.canvasAdd === true,
            });
          }}
          pendingMachineAssignment={pendingAssignment}
          onAssignmentApplied={() => setPendingAssignment(null)}
        />
      </div>

      {sidebarState ? (
        <MachineSidebar
          machineId={sidebarState.machineId}
          lineId={id}
          mode="edit"
          context={sidebarState.canvasAdd ? "canvas-add" : "line-edit"}
          provisoryNodeId={sidebarState.nodeId}
          onClose={() => setSidebarState(null)}
          onCancel={() => setSidebarState(null)}
          onMachineChange={(event) => {
            if (event.type === "created" && event.name) {
              setPendingAssignment({
                nodeId: sidebarState.nodeId,
                machineId: event.machineId,
                name: event.name,
              });
              setSidebarState((prev) =>
                prev ? { ...prev, machineId: event.machineId, canvasAdd: false } : null
              );
            } else if (event.type === "removed") {
              setSidebarState(null);
            }
          }}
        />
      ) : null}
    </section>
  );
}
